import { apiRequest } from '@/helpers/request'
import type { T_UniRes } from '@/types'
import { VISITOR_MESSAGE_API_METHODS } from './constants'
import {
  mapVisitorMessage,
  mapVisitorMessageTopic,
  mapVisitorMessageTopicEntry,
  mapVisitorUnreadMessage,
} from './mappers'
import type {
  T_VisitorMessage,
  T_VisitorMessageFindPayloadRaw,
  T_VisitorMessageListPayloadRaw,
  T_VisitorMessageRaw,
  T_VisitorMessageTopic,
  T_VisitorMessageTopicEntryRaw,
  T_VisitorMessageTopicEntry,
  T_VisitorMessageTopicsPayloadRaw,
  T_VisitorMessageTopicRaw,
  T_VisitorMessageUnreadListPayloadRaw,
  T_VisitorUnreadMessage,
} from './types'

type T_UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is T_UnknownRecord => {
  return typeof value === 'object' && value !== null
}

const unwrapLegacyData = <T>(response: unknown): T => {
  if (isRecord(response) && 'data' in response && response.data !== undefined) {
    return response.data as T
  }

  if (isRecord(response) && 'result' in response && response.result !== undefined) {
    return response.result as T
  }

  return response as T
}

const asArray = <T>(value: unknown): T[] => {
  return Array.isArray(value) ? (value as T[]) : []
}

const readPayloadField = <T>(payload: unknown, fieldName: string): T | undefined => {
  const record = isRecord(payload) ? payload : null
  if (record && fieldName in record) {
    return record[fieldName] as T
  }

  const result = record && isRecord(record.result) ? record.result : null
  if (result && fieldName in result) {
    return result[fieldName] as T
  }

  const data = record && isRecord(record.data) ? record.data : null
  if (data && fieldName in data) {
    return data[fieldName] as T
  }

  return undefined
}

const asNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const fetchVisitorMessageTopics = async (): Promise<T_VisitorMessageTopicEntry[]> => {
  const response = await apiRequest<T_UniRes<T_VisitorMessageTopicsPayloadRaw> | T_VisitorMessageTopicsPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.topics,
    param: {},
  })

  const payload = unwrapLegacyData<T_VisitorMessageTopicsPayloadRaw>(response)
  return asArray<T_VisitorMessageTopicEntryRaw>(readPayloadField(payload, 'list')).map(mapVisitorMessageTopicEntry)
}

export const findVisitorMessageTopics = async (text: string): Promise<T_VisitorMessageTopicEntry[]> => {
  const response = await apiRequest<T_UniRes<T_VisitorMessageTopicsPayloadRaw> | T_VisitorMessageTopicsPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.topicFind,
    param: {
      text,
    },
  })

  const payload = unwrapLegacyData<T_VisitorMessageTopicsPayloadRaw>(response)
  return asArray<T_VisitorMessageTopicEntryRaw>(readPayloadField(payload, 'list')).map(mapVisitorMessageTopicEntry)
}

export const fetchVisitorMessageList = async (topicId: number): Promise<{
  topic: T_VisitorMessageTopic
  messages: T_VisitorMessage[]
}> => {
  const response = await apiRequest<T_UniRes<T_VisitorMessageListPayloadRaw> | T_VisitorMessageListPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.list,
    param: {
      topic_id: topicId,
    },
  })

  const payload = unwrapLegacyData<T_VisitorMessageListPayloadRaw>(response)
  return {
    topic: mapVisitorMessageTopic(readPayloadField(payload, 'topic')),
    messages: asArray<T_VisitorMessageRaw>(readPayloadField(payload, 'messages')).map(mapVisitorMessage),
  }
}

export const findVisitorMessageById = async (messageId: number): Promise<{
  topic: T_VisitorMessageTopic
  message: T_VisitorMessage
} | null> => {
  if (!messageId) return null

  const response = await apiRequest<T_UniRes<T_VisitorMessageFindPayloadRaw> | T_VisitorMessageFindPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.find,
    param: {
      message_id: messageId,
    },
  })

  const payload = unwrapLegacyData<T_VisitorMessageFindPayloadRaw>(response)
  const message = readPayloadField<T_VisitorMessageRaw>(payload, 'message')
  if (!message) return null

  return {
    topic: mapVisitorMessageTopic(readPayloadField(payload, 'topic')),
    message: mapVisitorMessage(message),
  }
}

export const fetchVisitorMessageUnreadList = async (): Promise<T_VisitorUnreadMessage[]> => {
  const response = await apiRequest<T_UniRes<T_VisitorMessageUnreadListPayloadRaw> | T_VisitorMessageUnreadListPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.listUnread,
    param: {},
  })

  const payload = unwrapLegacyData<T_VisitorMessageUnreadListPayloadRaw>(response)
  return asArray<{ message?: T_VisitorMessageRaw; topic?: T_VisitorMessageTopicRaw }>(readPayloadField(payload, 'list'))
    .map((item) => mapVisitorUnreadMessage(item?.message, item?.topic))
    .filter((item) => item.id > 0)
}

export const sendVisitorMessage = async (
  topicId: number,
  text: string,
  fileIds: number[] = []
): Promise<T_VisitorMessage | null> => {
  const response = await apiRequest<T_UniRes<T_VisitorMessageFindPayloadRaw> | T_VisitorMessageFindPayloadRaw>({
    method: VISITOR_MESSAGE_API_METHODS.send,
    param: {
      topic_id: topicId,
      message: {
        text,
        files: fileIds,
      },
    },
  })

  const payload = unwrapLegacyData<T_VisitorMessageFindPayloadRaw>(response)
  const message = readPayloadField<T_VisitorMessageRaw>(payload, 'message')
  return message ? mapVisitorMessage(message) : null
}

export const markVisitorMessagesAsRead = async (messageIds: number[]): Promise<number[]> => {
  const safeIds = asArray<number>(messageIds)
    .map((id) => asNumber(id))
    .filter((id) => id > 0)

  if (!safeIds.length) return []

  await apiRequest({
    method: VISITOR_MESSAGE_API_METHODS.hadRead,
    param: {
      message_ids: safeIds,
    },
  })

  return safeIds
}
